import styled from 'styled-components'

import { calculateHouseholdIncomeThreshold } from 'app/components/Ballot/constants'
import { StyleTableContainer } from 'app/components/Ballot/Modals/BallotHouseholdIncomeModal'
import { Box, Flex, Modal } from 'app/components/design-system-components'
import { Text } from 'app/components/design-system-components/typography'
import theme from 'app/match/applicationReportPDF/assets/theme'

const StyledTableContainer = styled.table`
  margin-left: auto;
  margin-right: auto;
  text-align: center;
`
interface IncomeThresholdModalProps {
  toggleModal?: () => void
  returnAsComp?: boolean
  ballotsHouseholdIncomeLimit?: {
    type: string
    income: number
  }[]
  ballotHouseholdIncomeYear?: string
}

interface NameAndMembers {
  name: string
  adults: number
  children: number
  adder: number
}

const LOW_INCOME = 'LOW'
const MODERATE_INCOME = 'MODERATE'

const ADULT_1 = '1 Adult'
const ADULT_2 = '2 adults'
const ADULT_3 = '3 adults'
const SOLE_PARENT_1_CHILD = 'Sole parent, 1 child'
const SOLE_PARENT_2_CHILD = 'Sole parent, 2 child'
const SOLE_PARENT_3_CHILD = 'Sole parent, 3 child'
const COUPLE_1_CHILD = 'Couple, 1 child'
const COUPLE_2_CHILD = 'Couple, 2 children'
const COUPLE_3_CHILD = 'Couple, 3 children'

function IncomeTableModalQLD({
  toggleModal = () => {},
  returnAsComp = false,
  ballotsHouseholdIncomeLimit = [],
  ballotHouseholdIncomeYear = '',
}: IncomeThresholdModalProps) {
  let nameAndMembers: NameAndMembers[] = []

  // NAHC requested to change the decimals in this ticket: https://app.clickup.com/t/86cxr9rez
  // To account for the changes we need to add 0.01 to the adder

  if (ballotHouseholdIncomeYear.toUpperCase().includes(LOW_INCOME)) {
    nameAndMembers = [
      { name: ADULT_1, adults: 1, children: 0, adder: 0 },
      { name: ADULT_2, adults: 2, children: 0, adder: 0.01 },
      { name: ADULT_3, adults: 3, children: 0, adder: 0.01 },
      { name: SOLE_PARENT_1_CHILD, adults: 1, children: 1, adder: 0.01 },
      { name: SOLE_PARENT_2_CHILD, adults: 1, children: 2, adder: 0 },
      { name: SOLE_PARENT_3_CHILD, adults: 1, children: 3, adder: 0 },
      { name: COUPLE_1_CHILD, adults: 2, children: 1, adder: 0.01 },
      { name: COUPLE_2_CHILD, adults: 2, children: 2, adder: 0 },
      { name: COUPLE_3_CHILD, adults: 2, children: 3, adder: 0 },
    ]
  }

  if (ballotHouseholdIncomeYear.toUpperCase().includes(MODERATE_INCOME)) {
    nameAndMembers = [
      { name: ADULT_1, adults: 1, children: 0, adder: 0 },
      { name: ADULT_2, adults: 2, children: 0, adder: 0 },
      { name: ADULT_3, adults: 3, children: 0, adder: -0.01 },
      { name: SOLE_PARENT_1_CHILD, adults: 1, children: 1, adder: 0.01 },
      { name: SOLE_PARENT_2_CHILD, adults: 1, children: 2, adder: 0.01 },
      { name: SOLE_PARENT_3_CHILD, adults: 1, children: 3, adder: 0.01 },
      { name: COUPLE_1_CHILD, adults: 2, children: 1, adder: 0 },
      { name: COUPLE_2_CHILD, adults: 2, children: 2, adder: 0 },
      { name: COUPLE_3_CHILD, adults: 2, children: 3, adder: 0.01 },
    ]
  }

  const modalBody = (
    <Flex flexDirection="column">
      <StyleTableContainer>
        <StyledTableContainer>
          <tr>
            <th>Household Type</th>
            <th>Maximum Annual Limit</th>
          </tr>
          {nameAndMembers.map((item, index) => (
            <tr key={index}>
              <td>{item.name}</td>
              <td>
                $
                {(
                  calculateHouseholdIncomeThreshold(
                    item.adults,
                    item.children,
                    ballotsHouseholdIncomeLimit,
                  ) + item?.adder
                ).toFixed(2)}
              </td>
            </tr>
          ))}
        </StyledTableContainer>
      </StyleTableContainer>
      <Text as="p" fontSize={theme.fontSizes.pRegular14} mt={5}>
        1. These figures exclude overtime. <br />
        2. Figures are calculated as at 30 June 2024 and are subject to a 2.5%
        per annum escalation on 1 July each year
      </Text>
    </Flex>
  )

  if (returnAsComp) {
    return <Box>{modalBody}</Box>
  }

  return (
    <Modal
      modalHeading="Eligible income thresholds"
      modalBody={modalBody}
      modalFooter={undefined}
      primaryLabel={undefined}
      primaryAction={undefined}
      secondaryLabel={undefined}
      secondaryAction={undefined}
      toggleModal={toggleModal}
      showSecondaryButton={false}
      showPrimaryButton={false}
    />
  )
}

export default IncomeTableModalQLD
